import { Component, Inject } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { finalize, iif, takeUntil } from 'rxjs';
import { showToast } from '../../../../toast.helper';
import { BaseComponent } from '../../../base.component';
import { FinalReportResponse } from '../../../models/final-report-response.model';
import { CriterionsService } from '../../../services/criterions.service';
import { ItimadCriterionsService } from '../../../services/itimad-criterions.service';

@Component({
  selector: 'app-add-edit-response',
  templateUrl: './add-edit-response.dialog.html',
  styleUrls: ['./add-edit-response.dialog.scss']
})
export class AddEditResponseComponent extends BaseComponent {
  responses = ['accept', 'not_accept', 'accept_update'];
  form: FormGroup;
  isLoadingResults = false;
  modelId: number;

  get reasonsControl() { return this.form.get('reasons'); }
  get updatesControl() { return this.form.get('updates') as FormArray; }
  get updatesControls() { return (this.form.get('updates') as FormArray).controls as FormControl[]; }
  get responseDecisionValue() {
    return this.form.controls.responseDecision.value;
  }

  constructor(
    public dialogRef: MatDialogRef<AddEditResponseComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private fb: FormBuilder,
    private criterionsService: CriterionsService,
    private itimadCriterionsService: ItimadCriterionsService,
    private toastrService: ToastrService,
    private translateService: TranslateService) {
    super();
    this.initForm();
    this.registerUpdateFormValidation();
    this.getData();
  }

  addUpdate(value: string = '') {
    this.updatesControl.push(new FormControl({ value: value, disabled: this.data.viewOnly }, Validators.required));
  }
  removeUpdate(index: number) {
    this.updatesControl.removeAt(index);
  }

  save() {
    if (this.form.invalid) return;
    this.isLoadingResults = true;
    const model: FinalReportResponse = {
      id: this.modelId,
      visitKey: this.data.visitMetaData.visitKey,
      recommendationsSummaryId: this.data.summaryId,
      ...this.form.value
    };
    iif(() => !!this.modelId,
      this.itimadCriterionsService.editFinalReportResponses(model),
      this.itimadCriterionsService.createFinalReportResponses(model))
      .pipe(finalize(() => this.isLoadingResults = false))
      .subscribe(data => {
        this.dialogRef.close(data.id);
        showToast(this.toastrService,
          this.translateService.instant("forms.common.Criteria.Saved done"),
          this.translateService.instant("forms.common.Criteria.Saved Successfully"),
          false
        );
      });
  }
  cancel(): void {
    this.dialogRef.close();
  }

  private initForm() {
    this.form = this.fb.group({
      responseDecision: [{ value: '', disabled: this.data.viewOnly }, [Validators.required]],
      reasons: [{ value: '', disabled: this.data.viewOnly }],
      updates: this.fb.array([]),
    });
  }
  private registerUpdateFormValidation() {
    this.form.controls.responseDecision.valueChanges.subscribe(value => {
      if (value != 'accept') {
        this.reasonsControl.setValidators([Validators.required]);
      } else {
        this.reasonsControl.reset();
        this.reasonsControl.clearValidators();
      }
      this.reasonsControl.updateValueAndValidity();

      if (value == 'accept_update') {
        if (!this.updatesControl.length) this.addUpdate();
        this.updatesControl.setValidators([Validators.required]);
      } else {
        this.updatesControl.clear();
        this.updatesControl.clearValidators();
      }
      this.updatesControl.updateValueAndValidity();
    });
  }

  private getData() {
    if (!this.data.id) return;
    this.isLoadingResults = true;
    this.itimadCriterionsService.getFinalReportResponses(this.data.id)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.isLoadingResults = false)
      ).subscribe(data => {
        this.updateform(data);
      });
  }
  private updateform(data: FinalReportResponse) {
    this.modelId = data.id;
    this.form.patchValue({
      responseDecision: data.responseDecision,
      reasons: data.reasons
    });
    this.updatesControl.clear();
    data.updates.forEach((value, index) => {
      this.addUpdate(value);
    });
  }
}
